/**
 * Agile Defense brand color palette for Power BI / native reports.
 * Source: color-schema/palette.png (Brand Guidelines p.21)
 *
 * Usage:
 *   import { REPORT_PALETTE, AD_NAVY, AD_RED } from "../reportColorPalette";
 *   // or
 *   import { AD } from "../reportColorPalette";
 *   style={{ color: AD.navy, background: AD.sky }}
 */

/* ══════════════════════════════════════════════════════════════ */
/*  Brand swatches (exact values from palette)                    */
/* ══════════════════════════════════════════════════════════════ */

export const REPORT_PALETTE = {
  /** Primary dark blue — PMS 655C */
  navy: {
    hex: "#002554",
    rgb: { r: 0, g: 37, b: 84 },
    cmyk: { c: 100, m: 79, y: 12, k: 59 },
    pms: "655C",
    label: "Navy",
  },

  /** Brand red — PMS 1797C */
  red: {
    hex: "#D23C3A",
    rgb: { r: 210, g: 60, b: 58 },
    cmyk: { c: 2, m: 97, y: 85, k: 7 },
    pms: "1797C",
    label: "Red",
  },

  /** Accent sky blue */
  sky: {
    hex: "#2288C8",
    rgb: { r: 34, g: 136, b: 200 },
    cmyk: { c: 65, m: 25, y: 0, k: 22 },
    label: "Sky",
  },

  /** Accent gold / amber */
  gold: {
    hex: "#FAAE5B",
    rgb: { r: 250, g: 174, b: 91 },
    cmyk: { c: 0, m: 30, y: 64, k: 2 },
    label: "Gold",
  },

  /** Deep midnight navy */
  midnight: {
    hex: "#051435",
    rgb: { r: 5, g: 20, b: 53 },
    cmyk: { c: 100, m: 90, y: 45, k: 60 },
    label: "Midnight",
  },

  /** Mid slate blue-grey */
  slate: {
    hex: "#52687C",
    rgb: { r: 82, g: 104, b: 124 },
    cmyk: { c: 34, m: 10, y: 0, k: 60 },
    label: "Slate",
  },

  /** Light cool grey */
  mist: {
    hex: "#ADB8BF",
    rgb: { r: 173, g: 184, b: 191 },
    cmyk: { c: 7, m: 0, y: 0, k: 30 },
    label: "Mist",
  },
};

/* ══════════════════════════════════════════════════════════════ */
/*  Flat hex exports                                              */
/* ══════════════════════════════════════════════════════════════ */

export const AD_NAVY = REPORT_PALETTE.navy.hex;
export const AD_RED = REPORT_PALETTE.red.hex;
export const AD_SKY = REPORT_PALETTE.sky.hex;
export const AD_GOLD = REPORT_PALETTE.gold.hex;
export const AD_MIDNIGHT = REPORT_PALETTE.midnight.hex;
export const AD_SLATE = REPORT_PALETTE.slate.hex;
export const AD_MIST = REPORT_PALETTE.mist.hex;

/** Shorthand map of hex values only */
export const AD = {
  navy: AD_NAVY,
  red: AD_RED,
  sky: AD_SKY,
  gold: AD_GOLD,
  midnight: AD_MIDNIGHT,
  slate: AD_SLATE,
  mist: AD_MIST,
};

/* ══════════════════════════════════════════════════════════════ */
/*  Semantic aliases for reports                                  */
/* ══════════════════════════════════════════════════════════════ */

export const REPORT_COLORS = {
  /** Table / chart headers, primary brand fill */
  primary: AD_NAVY,
  /** Page chrome, dark panels, strong contrast */
  primaryDark: AD_MIDNIGHT,
  /** Links, series accent, highlights */
  accent: AD_SKY,
  /** Budget / forecast / warning series */
  warning: AD_GOLD,
  /** Negative variance, alerts */
  danger: AD_RED,
  /** Secondary text, axis labels, muted UI */
  muted: AD_SLATE,
  /** Borders, disabled, light fills */
  subtle: AD_MIST,
};

/** Standard multi-series chart colors (PY / CY Forecast / Budget) */
export const CHART_SERIES = {
  /** Prior year (FY - YYYY) */
  py: AD_MIST,
  /** Current year / CY Forecast */
  cy: AD_NAVY,
  /** Budget */
  budget: AD_RED,
};

/**
 * Multi-metric chart colors (Forecast Versions Comparison, etc.).
 * Strong brand swatches only — excludes mist / slate / midnight greys.
 */
export const CHART_METRIC_COLORS = [
  AD_SKY,
  AD_GOLD,
  AD_NAVY,
  AD_RED,
];

/**
 * Donut / categorical chart colors (LTM KPI, etc.).
 * High-contrast rank order so adjacent slices stay distinct:
 * sky → navy → gold → red → purple → teal → magenta → green → …
 * Avoids clustering muted blue-greys (slate / mist / midnight).
 */
export const CHART_DONUT_COLORS = [
  AD_SKY, // #2288C8
  AD_NAVY, // #002554
  AD_GOLD, // #FAAE5B
  AD_RED, // #D23C3A
  "#6B4C9A", // purple
  "#1A9B8E", // teal
  "#C44D7A", // magenta
  "#5FA777", // green
  "#8B7EC8", // lavender
  "#E07A5F", // coral
  AD_MIDNIGHT, // #051435
  AD_SLATE, // #52687C
  AD_MIST, // #ADB8BF
];

/** Fixed end-customer group colors for LTM KPI */
export const CHART_END_CUSTOMER_COLORS = {
  DOW: AD_NAVY,
  DHS: AD_SKY,
  "DOJ/FBI": AD_GOLD,
  FEDCIV: AD_RED,
  "(Blank)": AD_MIST,
};

export const COLOR_PRIMARY = REPORT_COLORS.primary;
export const COLOR_PRIMARY_DARK = REPORT_COLORS.primaryDark;
export const COLOR_ACCENT = REPORT_COLORS.accent;
export const COLOR_WARNING = REPORT_COLORS.warning;
export const COLOR_DANGER = REPORT_COLORS.danger;
export const COLOR_MUTED = REPORT_COLORS.muted;
export const COLOR_SUBTLE = REPORT_COLORS.subtle;

export const CHART_PY = CHART_SERIES.py;
export const CHART_CY = CHART_SERIES.cy;
export const CHART_BUDGET = CHART_SERIES.budget;
